/**
 * Камера/микрофон (один общий MediaStream на приложение).
 *
 *   import { acquireCamera, releaseCamera, cameraErrorMessage } from '@/services/media'
 *   const stream = await acquireCamera()   // переиспользует живой поток; бросает MediaAccessError
 *   releaseCamera()                         // остановить все треки (выход из игры/поиска)
 *
 * Ошибка: MediaAccessError { name:'MediaAccessError', code:<DOMException.name>, message:<русский текст> }.
 *
 * VUE_APP_DEV_TOOLS === 'true': если getUserMedia недоступен или упал — подставляется фейковая
 * камера из src/dev/fakeCamera.js (canvas.captureStream с анимированным «лицом»).
 * Принудительно включить фейковую камеру: добавить ?fakecam=1 к URL (только в dev tools).
 */

import diagnostics from "@/services/diagnostics";

export const CAMERA_CONSTRAINTS = Object.freeze({
  video: {
    facingMode: "user",
    width: { ideal: 640 },
    height: { ideal: 480 },
    frameRate: { ideal: 30, max: 30 },
  },
  audio: true,
});

const DEV_TOOLS = process.env.VUE_APP_DEV_TOOLS === "true";

const ERROR_MESSAGES = {
  NotAllowedError: "Нет доступа к камере. Разрешите доступ к камере и микрофону в настройках и попробуйте снова.",
  SecurityError: "Нет доступа к камере. Разрешите доступ к камере и микрофону в настройках и попробуйте снова.",
  NotFoundError: "Камера не найдена. Подключите камеру и попробуйте снова.",
  NotReadableError: "Камера занята другим приложением. Закройте его и попробуйте снова.",
  AbortError: "Не удалось запустить камеру. Попробуйте снова.",
  OverconstrainedError: "Камера не поддерживает нужный режим. Попробуйте другое устройство.",
  NotSupportedError: "Браузер не поддерживает доступ к камере. Откройте игру в Telegram или другом браузере.",
  TypeError: "Браузер не поддерживает доступ к камере. Откройте игру в Telegram или другом браузере.",
};

export class MediaAccessError extends Error {
  constructor(code, cause) {
    super(ERROR_MESSAGES[code] || "Не удалось включить камеру. Попробуйте снова.");
    this.name = "MediaAccessError";
    this.code = code || "UnknownError";
    this.cause = cause;
  }
}

/** Русский текст для ошибки getUserMedia / MediaAccessError. */
export function cameraErrorMessage(error) {
  if (error instanceof MediaAccessError) return error.message;
  const code = error && error.name;
  return ERROR_MESSAGES[code] || "Не удалось включить камеру. Попробуйте снова.";
}

let currentStream = null;
let pending = null;

diagnostics.registerSource("camera", () => {
  const video = currentStream?.getVideoTracks() || [];
  const audio = currentStream?.getAudioTracks() || [];
  const settings = video[0]?.getSettings?.() || {};
  return { streams: currentStream ? 1 : 0, pending: !!pending,
    videoTracks: video.filter(track => track.readyState === "live").length,
    audioTracks: audio.filter(track => track.readyState === "live").length,
    width: settings.width, height: settings.height, frameRate: settings.frameRate,
    readyState: video[0]?.readyState, muted: video[0]?.muted, enabled: video[0]?.enabled };
});

function isLive(stream) {
  return !!stream && stream.getVideoTracks().some((t) => t.readyState === "live");
}

function hasFakecamParam() {
  if (!DEV_TOOLS || typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).has("fakecam");
  } catch (e) {
    return false;
  }
}

// Флаг из URL при загрузке: переходы роутера (например, ?preview=… → /game) убирают query.
const FAKECAM_AT_START = hasFakecamParam();

function forceFake() {
  return FAKECAM_AT_START || hasFakecamParam();
}

async function createFake() {
  // Условие должно быть литеральным: webpack вырезает dev-чанк из прод-сборки.
  if (process.env.VUE_APP_DEV_TOOLS === "true") {
    const { createFakeCameraStream } = await import(/* webpackChunkName: "dev-fake-camera" */ "@/dev/fakeCamera");
    return createFakeCameraStream({ label: "Вы" });
  }
  throw new MediaAccessError("NotSupportedError");
}

async function requestReal() {
  if (typeof navigator === "undefined" || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    throw new MediaAccessError("NotSupportedError");
  }
  try {
    return await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
  } catch (error) {
    const name = error && error.name;
    if (name === "NotAllowedError" || name === "SecurityError") throw new MediaAccessError(name, error);
    // Ослабляем требования: только facingMode, затем без микрофона.
    try {
      return await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: true });
    } catch (e) {
      try {
        return await navigator.mediaDevices.getUserMedia({ video: CAMERA_CONSTRAINTS.video, audio: false });
      } catch (last) {
        throw new MediaAccessError((last && last.name) || name, last);
      }
    }
  }
}

/**
 * Получить поток камеры+микрофона. Повторные вызовы возвращают тот же живой поток.
 * @returns {Promise<MediaStream>}
 * @throws {MediaAccessError}
 */
export async function acquireCamera() {
  if (isLive(currentStream)) return currentStream;
  if (pending) return pending;
  pending = (async () => {
    releaseCamera();
    let stream;
    if (forceFake()) {
      stream = await createFake();
    } else {
      try {
        stream = await requestReal();
      } catch (error) {
        if (!DEV_TOOLS) throw error;
        console.warn("[media] getUserMedia недоступен, используем фейковую камеру (dev tools)", error);
        stream = await createFake();
      }
    }
    currentStream = stream;
    return stream;
  })();
  try {
    return await pending;
  } finally {
    pending = null;
  }
}

/** Остановить и забыть текущий поток. */
export function releaseCamera() {
  if (currentStream) {
    currentStream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (e) {
        // трек уже остановлен
      }
    });
  }
  currentStream = null;
}
